import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { SelectButton, SelectButtonChangeEvent } from "primereact/selectbutton";

import { faFileLines, faRightLeft, faText, IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { translationKeys } from "../../../models/app-translation-keys";
import { AllLanguageOptions } from "../../../models/constants/app-language-options.const";
import { ILanguageOption } from "../../../models/interfaces";
import { Device } from "../../../stores";
import { useStore } from "../../../stores/StoreContext";
import { GlossarySelection } from "./GlossarySelection/GlossarySelection";
import { IGlossaryFileSupportExtended } from "./GlossarySelection/GlossarySelection.types";

import "./TranslationHeader.scss";

export enum TranslationType {
  Text = "Text",
  File = "File",
}

interface ITranslationHeaderProps {
  sourceLanguage: ILanguageOption;
  targetLanguage: ILanguageOption;
  setSourceLanguage: (language: ILanguageOption) => void;
  setTargetLanguage: (language: ILanguageOption) => void;
  onTranslationTypeChange: (type: TranslationType) => void;
  onGlossaryChange: (glossary: IGlossaryFileSupportExtended | null) => void;
}

interface ITranslationTypeOption {
  label: string;
  labelShort: string;
  type: TranslationType;
  icon: IconDefinition;
}

const TranslationHeaderRaw: FunctionComponent<ITranslationHeaderProps> = (props: ITranslationHeaderProps) => {
  const { t } = useTranslation();
  const { userStore, breakpointStore } = useStore();

  const [selectedGlossary, setSelectedGlossary] = useState<IGlossaryFileSupportExtended | null>(null);

  const translationTypeKeys = translationKeys.feature.translation.type;

  const translationTypeOptions: ITranslationTypeOption[] = [
    {
      label: translationTypeKeys.text.label,
      labelShort: translationTypeKeys.text.labelShort,
      type: TranslationType.Text,
      icon: faText,
    },
    {
      label: translationTypeKeys.file.label,
      labelShort: translationTypeKeys.file.labelShort,
      type: TranslationType.File,
      icon: faFileLines,
    },
  ];
  const [selectedTranslationType, setSelectedTranslationType] = useState<ITranslationTypeOption>(
    translationTypeOptions[0],
  );

  const [filteredLanguageOptions, setFilteredLanguageOptions] = useState<ILanguageOption[]>([]);

  const [translatedLanguageOptions, setTranslatedLanguageOptions] = useState<ILanguageOption[]>([]);

  useEffect(() => {
    if (!selectedGlossary || selectedGlossary.supportedLanguages.length < 2) {
      setFilteredLanguageOptions(AllLanguageOptions);
      return;
    }

    setFilteredLanguageOptions(
      AllLanguageOptions.filter(lang => selectedGlossary.supportedLanguages.includes(lang.code)),
    );
  }, [selectedGlossary]);

  useEffect(() => {
    const translatedOptions = filteredLanguageOptions
      .map(lang => ({ ...lang, label: t(lang.labelKey) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    setTranslatedLanguageOptions(translatedOptions);
  }, [filteredLanguageOptions, userStore.currentLanguage]);

  useEffect(() => {
    props.onTranslationTypeChange(selectedTranslationType.type);
  }, [selectedTranslationType]);

  const handleLanguageSwitch = () => {
    const previousSourceLanguage = props.sourceLanguage;
    props.setSourceLanguage(props.targetLanguage);
    props.setTargetLanguage(previousSourceLanguage);
  };

  const onGlossaryChange = (
    glossary: IGlossaryFileSupportExtended | null,
    sourceLanguage: ILanguageOption,
    targetLanguage: ILanguageOption,
  ) => {
    setSelectedGlossary(glossary);
    props.onGlossaryChange(glossary);
    props.setSourceLanguage(sourceLanguage);
    props.setTargetLanguage(targetLanguage);
  };

  const LanguageDropdown = useCallback(
    (value: ILanguageOption, onChange: (value: ILanguageOption) => void, dropdownId: string) => {
      const foundValue = value?.code && translatedLanguageOptions.find(lang => lang.code === value.code);

      return (
        <Dropdown
          data-testid={dropdownId}
          value={foundValue}
          options={translatedLanguageOptions}
          onChange={e => {
            onChange(e.value as ILanguageOption);
          }}
          optionLabel="label"
          filter
          showFilterClear
          filterInputAutoFocus={true}
          filterBy="label"
          className="language-dropdown"
          virtualScrollerOptions={{
            itemSize: 36,
            showLoader: true,
          }}
        />
      );
    },
    [
      filteredLanguageOptions,
      translatedLanguageOptions,
      props.sourceLanguage,
      props.targetLanguage,
      userStore.currentLanguage,
    ],
  );

  const selectButtonItemTemplate = useCallback(
    (option: ITranslationTypeOption) => {
      return (
        <div className="flex align-items-center gap-2">
          <FontAwesomeIcon icon={option.icon} />
          {t(breakpointStore.isSmallerThan(Device.Tablet) ? option.labelShort : option.label)}
        </div>
      );
    },
    [userStore.currentLanguage, breakpointStore.device],
  );

  return (
    <div className="translation-header">
      <div className="translation-header-controls">
        <SelectButton
          id="showAsCheckboxes"
          value={selectedTranslationType}
          optionLabel={breakpointStore.isSmallerThan(Device.Tablet) ? "labelShort" : "label"}
          onChange={(e: SelectButtonChangeEvent) => !!e.value && setSelectedTranslationType(e.value)}
          options={translationTypeOptions}
          itemTemplate={selectButtonItemTemplate}
        />

        <div className="glossary-selection">
          <GlossarySelection
            sourceLanguage={props.sourceLanguage}
            targetLanguage={props.targetLanguage}
            onGlossaryChange={onGlossaryChange}
          />
        </div>
      </div>

      <div className="language-selection-container">
        <div className="source-language-container">
          {LanguageDropdown(props.sourceLanguage, props.setSourceLanguage, "source-language-dropdown")}
        </div>

        <div className="switch-languages-button-container">
          <Button
            icon={<FontAwesomeIcon icon={faRightLeft} />}
            onClick={handleLanguageSwitch}
            rounded
            text
            aria-label="Switch Languages"
          />
        </div>

        <div className="target-language-container">
          {LanguageDropdown(props.targetLanguage, props.setTargetLanguage, "target-language-dropdown")}
        </div>
      </div>
    </div>
  );
};

export const TranslationHeader = observer(TranslationHeaderRaw);
